/*-------------------------------------------*/
/*----------IMPORTS AND INITIALIZERS---------*/
/*-------------------------------------------*/

// Imports
import { 
    ChevronLeft, ChevronRight, 
    Clear, KeyboardArrowDown, 
    KeyboardArrowUp, Search 
} from "@mui/icons-material";
import { 
    Card, CardContent, CardHeader, 
    Collapse, Divider, Grid, 
    IconButton, Toolbar, Tooltip 
} from "@mui/material";
import { useEffect, useState } from "react";
import TableOfRecords from "./TableOfRecords";
import styled from 'styled-components';
import { Form, InputGroup } from "react-bootstrap";

/*-------------------------------------------*/
/*------------PRE-CONFIG INTERFACES----------*/
/*-------------------------------------------*/

// For styling react-bootstrap form.control field(s)
const StyledFormControl = styled(Form.Control)`
    height: 40px;
    border-color: lightgray;
    border-right-color: transparent;
    &:focus {
        box-shadow: none;
        border-color: lightgray;
        border-right-color: transparent;
    }
`;

// Interface for defining table configuration type
interface KeyValueType {
    [key: string]: any;
}

// Part of type definition for props
interface Props {
    open: boolean, drawerWidth: number,
    showSkeleton: boolean, setShowSkeleton: (bool: boolean) => void,
    fetchedFilterRecords: KeyValueType[], setFetchedFilteredRecords: (result: KeyValueType[]) => void,
    openTableCard: boolean, setOpenTableCard: (openTableCard: boolean) => void,
    secondarySearchRecords: KeyValueType[], setSecondarySearchRecords: (result: KeyValueType[]) => void
    recordsToRender: KeyValueType[], setRecordsToRender: (result: KeyValueType[]) => void
    tablePageNumber: boolean, setTablePageNumber: (bool: boolean) => void
}

/*------------------------------------------*/
/*-----------------COMPONENT----------------*/
/*------------------------------------------*/

const RenderRecords: React.FC<Props> = ({
    open, drawerWidth,
    showSkeleton, setShowSkeleton,
    fetchedFilterRecords, setFetchedFilteredRecords,
    openTableCard, setOpenTableCard,
    secondarySearchRecords, setSecondarySearchRecords,
    recordsToRender, setRecordsToRender,
    tablePageNumber, setTablePageNumber
}) => {

    // For handling form field to search among the rendered records
    const [searchText, setSearchText] = useState<string>("")
    const handleTableRecordsSearch = (searchValue: string) => {

        setSearchText(searchValue)
        if (searchValue !== "") {
            if (tablePageNumber === false) {
                const filteredRows = fetchedFilterRecords.filter((row) =>
                    Object.values(row).some(
                    (value) =>
                        typeof value === 'string' && value.toLowerCase().includes(searchValue.toLowerCase())
                    )
                );
                setRecordsToRender(filteredRows)
            } else {
                const filteredRows = secondarySearchRecords.filter((row) =>
                    Object.values(row).some(
                    (value) =>
                        typeof value === 'string' && value.toLowerCase().includes(searchValue.toLowerCase())
                    )
                );
                setRecordsToRender(filteredRows)
            }
        } else {
            if (tablePageNumber === false) {
                setRecordsToRender(fetchedFilterRecords)
            } else {
                setRecordsToRender(secondarySearchRecords)
            }
        }
    }

    // Page 2 title
    const [renderEntName, setRenderEntName] = useState(false)
    const [enterpriseName, setEnterpriseName] = useState<string>("")

    // If the table card is closed, set search text to an empty string
    useEffect(() => {
        if (openTableCard === false) setSearchText("")
    }, [openTableCard, tablePageNumber])

    /*------------------*/
    /*-RENDER COMPONENT-*/
    /*------------------*/

    return (
        <Card
            sx={{
                boxShadow: 'none',
                border: "1px solid lightgray",
                maxWidth: open ? `calc(100vw - ${drawerWidth}px)` : `calc(200vw - ${drawerWidth}px)`
            }}
        > 

            {/* Render card header */}
            <CardHeader 
                title = 'Number List'
                titleTypographyProps={{ variant: 'h6', sx: { fontWeight: 'bold' }}}
                action={ 
                <IconButton 
                    onClick={() => setOpenTableCard(!openTableCard)} 
                    aria-label="expand"
                    size="small"
                > 
                    {openTableCard ? <KeyboardArrowUp /> 
                    : <KeyboardArrowDown />} 
                </IconButton> 
                } 
            >
            </CardHeader> 

            {/* Render collapsible card content */}
            <Collapse in={openTableCard} timeout="auto" unmountOnExit> 
                
                <Divider sx={{ bgcolor: '#1e1d31' }}/>

                {/* Render card contents */}
                <CardContent>

                    {/* Render toolbar */}
                    <Toolbar>
                        <Grid container direction='row'>

                            {/* Render back and forward navigation icon buttons */}
                            <Grid item>
                                {
                                    tablePageNumber ? 
                                        <Grid item>
                                            <Tooltip title="Back" placement="top">
                                                <IconButton onClick={() => {
                                                    setTablePageNumber(false)
                                                    setRenderEntName(false)
                                                }}>
                                                    <ChevronLeft/>
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton onClick={() => {
                                                    setTablePageNumber(true)
                                                    setRenderEntName(true)
                                                }} disabled>
                                                <ChevronRight/>
                                            </IconButton>
                                        </Grid>
                                    : secondarySearchRecords.length !== 0 ?
                                        <Grid item>
                                            <IconButton onClick={() => {
                                                    setTablePageNumber(false)
                                                    setRenderEntName(false)
                                                }} disabled>
                                                <ChevronLeft/>
                                            </IconButton>
                                            <Tooltip title="Forward" placement="top">
                                                <IconButton onClick={() => {
                                                    setTablePageNumber(true)
                                                    setRenderEntName(true)
                                                    }}>
                                                    <ChevronRight/>
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                        :
                                        <Grid item>
                                            <IconButton onClick={() => {
                                                setTablePageNumber(false)
                                                setRenderEntName(false)
                                            }} disabled><ChevronLeft/></IconButton>
                                            <IconButton onClick={() => {
                                                setTablePageNumber(true)
                                                setRenderEntName(true)
                                            }} disabled><ChevronRight/></IconButton>
                                        </Grid>
                                }
                            </Grid>

                            {/* Render search form field */}
                            <Grid item>
                                <InputGroup>
                                    <StyledFormControl
                                        value={searchText}
                                        onChange={(e) => handleTableRecordsSearch(e.target.value)}
                                        placeholder="Search..."
                                    />
                                    {
                                        searchText ? (
                                            <IconButton 
                                                sx={{ 
                                                    backgroundColor: 'white',
                                                    border: '1px solid lightgray',
                                                    borderTopRightRadius: '6px',
                                                    borderBottomRightRadius: '6px',
                                                    borderLeft: 'none',
                                                    width: '30px',
                                                    height: '40px'
                                                }}
                                                onClick={() => handleTableRecordsSearch("")}
                                            >
                                                <Clear sx={{opacity: 0.5}}/>
                                            </IconButton>
                                        ) : <IconButton 
                                                sx={{ 
                                                    backgroundColor: 'white',
                                                    border: '1px solid lightgray',
                                                    borderTopRightRadius: '6px',
                                                    borderBottomRightRadius: '6px',
                                                    borderLeft: 'none',
                                                    width: '30px',
                                                    height: '40px'
                                                }}
                                            >
                                                <Search sx={{opacity: 0.5}}/>
                                            </IconButton>
                                    }
                                </InputGroup>
                            </Grid>

                        </Grid>
                    </Toolbar>

                    {/* Render the table */}
                    <TableOfRecords
                        open={open} drawerWidth={drawerWidth}
                        showSkeleton={showSkeleton} setShowSkeleton={setShowSkeleton}
                        fetchedFilterRecords={fetchedFilterRecords} setFetchedFilteredRecords={setFetchedFilteredRecords}
                        secondarySearchRecords={secondarySearchRecords} setSecondarySearchRecords={setSecondarySearchRecords}
                        recordsToRender={recordsToRender} setRecordsToRender={setRecordsToRender}
                        tablePageNumber={tablePageNumber} setTablePageNumber={setTablePageNumber}
                        enterpriseName={enterpriseName} setEnterpriseName={setEnterpriseName}
                        renderEntName={renderEntName} setRenderEntName={setRenderEntName}
                    />
                    
                </CardContent> 
            </Collapse> 
        </Card>
    );
}
 
export default RenderRecords;